import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Button,
  Descriptions,
  Form,
  PageHeader,
  Select,
  Table,
  Upload,
  message,
} from "antd";
import * as XLSX from "xlsx-js-style";
import { professionalAction } from "../../_actions";
import { imageProvisionService } from "../../_services/imageProvision.service";

const { Option } = Select;

const cellText = (value) => (value == null ? "" : String(value).trim());

const readCdwaExcel = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const workbook = XLSX.read(reader.result, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const grid = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          raw: false,
        });
        const headerIndex = grid.findIndex((row) =>
          row.some((cell) => cellText(cell) === "Inquiry ID")
        );
        if (headerIndex < 0) {
          reject(new Error("Inquiry ID column was not found"));
          return;
        }
        const headers = grid[headerIndex].map(cellText);
        const rows = grid
          .slice(headerIndex + 1)
          .map((row) => {
            const data = {};
            headers.forEach((header, index) => {
              if (header) data[header] = cellText(row[index]);
            });
            const enquiryId = data["Inquiry ID"];
            if (!enquiryId) return null;
            return { enquiryId, rows: data };
          })
          .filter(Boolean);
        if (!rows.length) {
          reject(new Error("Inquiry ID is required"));
          return;
        }
        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Excel file could not be read"));
    reader.readAsArrayBuffer(file);
  });

const ImageProvisionPage = ({ user }) => {
  const [form] = Form.useForm();
  const [units, setUnits] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadList = () => {
    setLoading(true);
    imageProvisionService
      .list()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((error) => message.error(error || "List failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    professionalAction
      .getActiveCities()
      .then((response) => {
        if (!response || !response.text) return;
        response.text().then((text) => {
          const parsed = JSON.parse(text);
          setUnits(Array.isArray(parsed) ? parsed : []);
        });
      })
      .catch(() => {});
    loadList();
  }, []);

  const onFinish = (values) => {
    setSaving(true);
    imageProvisionService
      .allocate({ city: values.city })
      .then(() => {
        message.success("City provisioned");
        form.resetFields();
        loadList();
      })
      .catch((error) => message.error(error || "Provision failed"))
      .finally(() => setSaving(false));
  };

  const removeRow = (id) => {
    imageProvisionService
      .remove({ id })
      .then(() => {
        message.success("Provision removed");
        loadList();
      })
      .catch((error) => message.error(error || "Remove failed"));
  };

  const uploadExcel = (file) => {
    setUploading(true);
    readCdwaExcel(file)
      .then((rows) => {
        const chunkSize = 200;
        const chunks = [];
        for (let index = 0; index < rows.length; index += chunkSize) {
          chunks.push(rows.slice(index, index + chunkSize));
        }
        return chunks.reduce(
          (chain, chunk) => chain.then(() => imageProvisionService.saveCdwa(chunk)),
          Promise.resolve()
        ).then(() => rows.length);
      })
      .then((count) => {
        message.success(`${count} inquiries saved`);
      })
      .catch((error) => message.error(error || "Excel upload failed"))
      .finally(() => setUploading(false));
    return false;
  };

  if (!user || `${user.role}` !== "8") {
    return <div>Third Party Image Provision is available for role 8.</div>;
  }

  return (
    <div>
      <PageHeader ghost={false} title={user.userid} subTitle="( SUPER ADMIN )">
        <Descriptions size="small" column={1}>
          <Descriptions.Item label="राज्य">महाराष्ट्र</Descriptions.Item>
          <Descriptions.Item>
            <span style={{ fontSize: 25, fontWeight: 600 }}>
              Third Party Image Provision
            </span>
          </Descriptions.Item>
        </Descriptions>
      </PageHeader>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ maxWidth: 420, marginTop: 16 }}
      >
        <Form.Item
          label="Select Unit"
          name="city"
          rules={[{ required: true, message: "Please select a unit" }]}
        >
          <Select
            placeholder="Please select Unit"
            showSearch
            optionFilterProp="children"
          >
            {units.map((unit) => {
              const cityId = unit.cityId;
              const cityName =
                unit.city_name && unit.city_name[0]
                  ? unit.city_name[0].City
                  : "";
              const taken = rows.some((row) => row.city === cityId);
              return (
                <Option key={cityId} value={cityId} disabled={taken}>
                  {cityName}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            Provision
          </Button>
        </Form.Item>
      </Form>
      <Upload
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={uploadExcel}
      >
        <Button loading={uploading} style={{ marginBottom: 16 }}>
          Excel Upload
        </Button>
      </Upload>
      <Table
        bordered
        loading={loading}
        rowKey="id"
        dataSource={rows.map((row, index) => ({ ...row, srNo: index + 1 }))}
        columns={[
          { title: "SrNo", dataIndex: "srNo", width: 80 },
          { title: "घटक", dataIndex: "cityName" },
          {
            title: "Action",
            render: (_, record) => (
              <Button onClick={() => removeRow(record.id)}>Remove</Button>
            ),
          },
        ]}
      />
    </div>
  );
};

function mapStateToProps(state) {
  const { authentication } = state;
  return { user: authentication.user };
}

const connectedImageProvision = connect(mapStateToProps)(ImageProvisionPage);
export { connectedImageProvision as ImageProvision };
